import {
  Activity,
  BarChart3,
  CircleHelp,
  Clock3,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "@/components/ui/card";

type ChartRow = Record<string, unknown>;

type TelemedicineOverview = {
  population?: Record<string, unknown> | null;
  access?: Record<string, unknown> | null;
  experience?: Record<string, unknown> | null;
  risk?: Record<string, unknown> | null;
} | null;

type TelemedicineBreakdowns = {
  specialties?: ChartRow[] | null;
  services?: ChartRow[] | null;
  riskDomains?: ChartRow[] | null;
} | null;

type TelemedicineModulePanelsProps = {
  activeSectionId: string;
  accentColor: string;
  moduleLabel: string;
  overview: TelemedicineOverview;
  timeline: ChartRow[] | null | undefined;
  breakdowns: TelemedicineBreakdowns;
  isLoading?: boolean;
};

const PIE_COLORS = ["#0f172a", "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

function toNumber(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toLabel(value: unknown, fallback = "Sem rótulo") {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number") return String(value);
  return fallback;
}

function formatInteger(value: unknown) {
  return toNumber(value).toLocaleString("pt-BR");
}

function formatDecimal(value: unknown, digits = 1) {
  return toNumber(value).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
  return (
    <Card className="rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50 p-8 text-center shadow-sm">
      <BarChart3 className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-4 text-lg font-semibold text-slate-950">{title}</p>
      <p className="mt-3 text-sm leading-7 text-slate-600">{description}</p>
    </Card>
  );
}

function LoadingState() {
  return (
    <Card className="rounded-[1.5rem] border border-slate-200 p-8 shadow-sm">
      <div className="flex items-center gap-3 text-slate-500">
        <Activity className="h-5 w-5" />
        <p className="text-sm font-medium">Consolidando os painéis executivos da Telemedicina...</p>
      </div>
    </Card>
  );
}

function ChartShell({
  title,
  description,
  insight,
  accentColor,
  children,
  footer,
}: {
  title: string;
  description: string;
  insight: string;
  accentColor: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden rounded-[1.6rem] border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
      <div className="border-b border-slate-100 bg-[linear-gradient(180deg,rgba(248,250,252,0.98)_0%,rgba(241,245,249,0.92)_100%)] px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-white bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" style={{ color: accentColor }} />
              leitura executiva
            </div>
            <h3 className="text-base font-semibold text-slate-950">{title}</h3>
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
            title={`${description}${insight ? `\n\n${insight}` : ""}`}
            aria-label={`Mais informações sobre ${title}`}
          >
            <CircleHelp className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-5 py-4">
        {children}
        {footer ? <div className="mt-4">{footer}</div> : null}
      </div>
    </Card>
  );
}

function SummaryStrip({ activeSectionId, overview }: { activeSectionId: string; overview: TelemedicineOverview }) {
  const cards = [
    {
      title: "Seção ativa",
      value:
        activeSectionId === "producao"
          ? "Produção médica"
          : activeSectionId === "eficiencia"
            ? "Eficiência"
            : activeSectionId === "no-show"
              ? "No-show"
              : "Visão geral",
      description: "A barra lateral organiza o recorte executivo do módulo e evita poluição no conteúdo central.",
      icon: Sparkles,
    },
    {
      title: "Conversão média",
      value: `${formatDecimal(toNumber(overview?.access?.conversionRate) * 100, 1)}%`,
      description: "Transformação da intenção assistencial em atendimento realizado.",
      icon: Activity,
    },
    {
      title: "Espera média",
      value: `${formatDecimal(overview?.access?.avgWaitMinutes, 1)} min`,
      description: "Tempo médio entre agendamento e atendimento concluído.",
      icon: Clock3,
    },
    {
      title: "Risco relevante",
      value: formatInteger(overview?.risk?.highRiskLives),
      description: "Vidas com maior prioridade clínica dentro do recorte atual.",
      icon: BarChart3,
    },
  ];

  return (
    <Card className="overflow-hidden rounded-[1.6rem] border border-slate-200 bg-white shadow-sm">
      <div className="grid gap-3 p-4 lg:grid-cols-4">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className="rounded-[1.2rem] border border-slate-100 bg-[linear-gradient(180deg,rgba(255,255,255,1)_0%,rgba(248,250,252,0.95)_100%)] p-3.5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{card.title}</p>
                  <p className="mt-1.5 text-sm font-semibold text-slate-950">{card.value}</p>
                </div>
                <div className="rounded-2xl px-2.5 py-2" style={{ backgroundColor: index % 2 === 0 ? "rgba(192,255,0,0.12)" : "rgba(15,23,42,0.06)" }}>
                  <Icon className="h-4 w-4 text-slate-700" />
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ExecutiveCards({ overview, accentColor }: { overview: TelemedicineOverview; accentColor: string }) {
  const cards = [
    {
      title: "Vidas elegíveis",
      value: overview?.population?.eligibleLives,
      description: "Base disponível para telemedicina no período selecionado.",
      icon: Users,
    },
    {
      title: "Usuários ativos",
      value: overview?.population?.activeUsers,
      description: "Pessoas que realmente utilizaram o módulo no recorte atual.",
      icon: Activity,
    },
    {
      title: "Atendimentos",
      value: overview?.access?.encountersTotal,
      description: "Produção concluída, consolidando o volume efetivamente assistido.",
      icon: BarChart3,
    },
    {
      title: "No-show",
      value: `${formatDecimal(toNumber(overview?.access?.noShowRate) * 100, 1)}%`,
      description: "Taxa de perda de agenda no período selecionado.",
      icon: Clock3,
    },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.title} className="overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white shadow-sm">
            <div className="h-1.5 bg-[linear-gradient(90deg,#C0FF00_0%,#0f172a_100%)]" />
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{card.title}</p>
                  <p className="mt-2 text-2xl font-bold text-slate-950">{typeof card.value === "string" ? card.value : formatInteger(card.value)}</p>
                </div>
                <div className="rounded-2xl px-2.5 py-2" style={{ backgroundColor: `${accentColor}14` }}>
                  <Icon className="h-4 w-4" style={{ color: accentColor }} />
                </div>
              </div>

            </div>
          </Card>
        );
      })}
    </div>
  );
}

function TimelineEfficiencyChart({
  data,
  title,
  description,
  accentColor,
}: {
  data: ChartRow[];
  title: string;
  description: string;
  accentColor: string;
}) {
  const normalized = data.map((row) => ({
    label: toLabel(row.metricDate),
    encounters: toNumber(row.encountersTotal),
    appointments: toNumber(row.appointmentsTotal),
    noShow: toNumber(row.noShowTotal),
    conversionRate: toNumber(row.conversionRate) * 100,
  }));

  if (!normalized.some((row) => row.encounters > 0 || row.appointments > 0 || row.noShow > 0)) {
    return <EmptyPanel title={title} description={description} />;
  }

  const peak = normalized.reduce((best, item) => (item.encounters > best.encounters ? item : best), normalized[0]);
  const insight = `Maior volume em ${peak.label}, com ${formatInteger(peak.encounters)} atendimentos e conversão de ${formatDecimal(peak.conversionRate, 1)}%.`;

  return (
    <ChartShell title={title} description={description} insight={insight} accentColor={accentColor}>
      <div className="h-[284px] w-full rounded-[1.3rem] bg-[radial-gradient(circle_at_top_left,rgba(192,255,0,0.14),transparent_30%),linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={normalized}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
            />
            <Bar dataKey="encounters" name="Atendimentos" fill={accentColor} radius={[8, 8, 4, 4]} />
            <Line type="monotone" dataKey="appointments" name="Agendamentos" stroke="#0f172a" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="conversionRate" name="Conversão (%)" stroke="#10b981" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function ComparativeBars({
  title,
  description,
  data,
  labelKey,
  primaryKey,
  secondaryKey,
  accentColor,
}: {
  title: string;
  description: string;
  data: ChartRow[];
  labelKey: string;
  primaryKey: string;
  secondaryKey?: string;
  accentColor: string;
}) {
  const normalized = data.map((row) => ({
    label: toLabel(row[labelKey]),
    primary: toNumber(row[primaryKey]),
    secondary: secondaryKey ? toNumber(row[secondaryKey]) : 0,
  }));

  if (!normalized.some((row) => row.primary > 0 || row.secondary > 0)) {
    return <EmptyPanel title={title} description={description} />;
  }

  const peak = normalized.reduce((best, item) => (item.primary > best.primary ? item : best), normalized[0]);
  const insight = secondaryKey
    ? `${peak.label} lidera o principal com ${formatDecimal(peak.primary, 1)} e contrapõe ${formatDecimal(peak.secondary, 1)} no eixo complementar.`
    : `${peak.label} lidera este recorte com ${formatDecimal(peak.primary, 1)} no período.`;

  return (
    <ChartShell title={title} description={description} insight={insight} accentColor={accentColor}>
      <div className="h-[284px] w-full rounded-[1.3rem] bg-[linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={normalized}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
            />
            <Bar dataKey="primary" name="Principal" fill={accentColor} radius={[8, 8, 4, 4]} />
            {secondaryKey ? <Bar dataKey="secondary" name="Complementar" fill="#0f172a" radius={[8, 8, 4, 4]} /> : null}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function RiskAndExperience({
  overview,
  breakdowns,
  accentColor,
}: {
  overview: TelemedicineOverview;
  breakdowns: TelemedicineBreakdowns;
  accentColor: string;
}) {
  const riskData = (breakdowns?.riskDomains ?? []).map((row) => ({
    name: toLabel(row.riskDomain, "Sem domínio"),
    value: toNumber(row.highRiskLives) + toNumber(row.criticalRiskLives),
  })).filter((row) => row.value > 0);

  const experienceData = [
    { label: "Resposta", value: toNumber(overview?.experience?.responseRate) * 100, suffix: "%" },
    { label: "Nota média", value: toNumber(overview?.experience?.avgScore) },
    { label: "NPS", value: toNumber(overview?.experience?.npsScore) },
  ];

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <ChartShell
        title="Distribuição de risco"
        description="Leitura rápida dos domínios com maior concentração de vidas sensíveis no recorte atual."
        insight={riskData.length ? `${riskData[0].name} é um dos domínios mais relevantes do recorte atual.` : "Ainda não há risco consolidado suficiente neste recorte."}
        accentColor={accentColor}
        footer={riskData.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {riskData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                  <span className="text-sm font-medium text-slate-700">{item.name}</span>
                </div>
                <span className="text-sm font-semibold text-slate-950">{formatInteger(item.value)}</span>
              </div>
            ))}
          </div>
        ) : undefined}
      >
        {riskData.length ? (
          <div className="h-[260px] w-full rounded-[1.3rem] bg-[linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  contentStyle={{
                    borderRadius: "1rem",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
                  }}
                />
                <Pie data={riskData} dataKey="value" nameKey="name" innerRadius={56} outerRadius={96} paddingAngle={3}>
                  {riskData.map((item, index) => (
                    <Cell key={`${item.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
            Ainda não há risco consolidado suficiente neste recorte.
          </div>
        )}
      </ChartShell>

      <ChartShell
        title="Experiência e eficiência"
        description="Bloco compacto para leitura simultânea de resposta, satisfação e percepção do canal digital."
        insight="Este bloco aproxima experiência do beneficiário da cadência operacional, reduzindo a fragmentação visual da tela."
        accentColor={accentColor}
      >
        <div className="grid gap-3">
          {experienceData.map((item) => (
            <div key={item.label} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-medium text-slate-700">{item.label}</p>
                <p className="text-lg font-semibold text-slate-950">{formatDecimal(item.value, 1)}{item.suffix ?? ""}</p>
              </div>
            </div>
          ))}
          <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600">
            A leitura executiva cruza desempenho operacional com percepção do beneficiário sem exigir múltiplos blocos dispersos na dobra superior.
          </div>
        </div>
      </ChartShell>
    </div>
  );
}

export default function TelemedicineModulePanels({
  activeSectionId,
  accentColor,
  moduleLabel,
  overview,
  timeline,
  breakdowns,
  isLoading = false,
}: TelemedicineModulePanelsProps) {
  if (isLoading) {
    return <LoadingState />;
  }

  const specialtyRows = breakdowns?.specialties ?? [];
  const serviceRows = breakdowns?.services ?? [];
  const timelineRows = timeline ?? [];

  return (
    <div className="space-y-6">
      <SummaryStrip activeSectionId={activeSectionId} overview={overview} />
      <ExecutiveCards overview={overview} accentColor={accentColor} />

      {(activeSectionId === "visao-geral" || activeSectionId === "producao") ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <TimelineEfficiencyChart
            data={timelineRows}
            title={`Evolução executiva de ${moduleLabel}`}
            description="Leitura combinada de agendamentos, atendimentos e conversão para evitar quadros separados com baixa densidade útil."
            accentColor={accentColor}
          />
          <ComparativeBars
            title="Especialidades com maior volume"
            description="Volume assistencial e encaminhamentos nas especialidades mais representativas do recorte atual."
            data={specialtyRows}
            labelKey="specialtyName"
            primaryKey="encountersTotal"
            secondaryKey="referralTotal"
            accentColor={accentColor}
          />
          <ComparativeBars
            title="Serviços com maior pressão de agenda"
            description="Compara agendamentos e perdas de agenda para evidenciar onde a operação cresce com mais atrito."
            data={serviceRows}
            labelKey="serviceName"
            primaryKey="appointmentsTotal"
            secondaryKey="noShowTotal"
            accentColor={accentColor}
          />
          <ComparativeBars
            title="Especialidades com maior saída clínica"
            description="Leitura de prescrições e encaminhamentos para ampliar a visão executiva além do volume bruto de atendimentos."
            data={specialtyRows}
            labelKey="specialtyName"
            primaryKey="prescriptionTotal"
            secondaryKey="referralTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {(activeSectionId === "visao-geral" || activeSectionId === "eficiencia") ? (
        <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-2">
            <ComparativeBars
              title="Eficiência por serviço"
              description="Comparativo entre produção e no-show nos recortes disponíveis, útil para identificar pressão operacional e perda de agenda."
              data={serviceRows}
              labelKey="serviceName"
              primaryKey="encountersTotal"
              secondaryKey="noShowRate"
              accentColor={accentColor}
            />
            <ComparativeBars
              title="Conversão e no-show por serviço"
              description="Leitura cruzada de taxa de conversão e taxa de perda para orientar as frentes mais críticas de eficiência operacional."
              data={serviceRows}
              labelKey="serviceName"
              primaryKey="conversionRate"
              secondaryKey="noShowRate"
              accentColor={accentColor}
            />
          </div>
          <RiskAndExperience overview={overview} breakdowns={breakdowns} accentColor={accentColor} />
        </div>
      ) : null}

      {activeSectionId === "no-show" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <TimelineEfficiencyChart
            data={timelineRows}
            title="Agendas perdidas e conversão"
            description="Bloco direcionado à leitura de no-show, cruzando perda de agenda, produção entregue e taxa de conversão ao longo do período."
            accentColor={accentColor}
          />
          <ComparativeBars
            title="Serviços com maior perda relativa"
            description="Comparativo por serviço, útil para orientar ações de confirmação, lembretes e recuperação de agenda."
            data={serviceRows}
            labelKey="serviceName"
            primaryKey="noShowRate"
            secondaryKey="encountersTotal"
            accentColor={accentColor}
          />
          <ComparativeBars
            title="Volume absoluto de no-show"
            description="Mostra onde a perda de agenda pesa mais em termos absolutos, sem esconder os serviços com maior massa de atendimento."
            data={serviceRows}
            labelKey="serviceName"
            primaryKey="noShowTotal"
            secondaryKey="appointmentsTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {!['visao-geral', 'producao', 'eficiencia', 'no-show'].includes(activeSectionId) ? (
        <EmptyPanel title="Seção em preparação" description="A arquitetura do módulo já suporta expansão futura do repertório executivo sem depender de nova reorganização da lateral." />
      ) : null}
    </div>
  );
}
